import React, { useMemo } from "react";
import { Card, Skeleton, Typography } from "antd";
import { ArrowUpOutlined, ArrowDownOutlined } from "@ant-design/icons";
import NPSAnimatedEmoji from "./NPSAnimatedEmoji";
import "./NPSGauge.css";

const { Text } = Typography;

const CX = 150;
const CY = 150;
const R = 118;
const STROKE = 14;

// Fraction (0-1) across the -100..+100 scale -> point on the semicircle.
const pointAt = (fraction) => {
  const angle = Math.PI * (1 - fraction);
  return [CX + R * Math.cos(angle), CY - R * Math.sin(angle)];
};

const arcPath = (from, to) => {
  const [x1, y1] = pointAt(from);
  const [x2, y2] = pointAt(to);
  return `M ${x1} ${y1} A ${R} ${R} 0 0 1 ${x2} ${y2}`;
};

const NPSGauge = ({ score, deltaVsLastMonth, averageScore, isLoading }) => {
  const fraction = (score + 100) / 200;
  const [knobX, knobY] = useMemo(() => pointAt(fraction), [fraction]);
  const trending = deltaVsLastMonth >= 0;

  if (isLoading) {
    return (
      <Card className="nps-gauge-card" bordered>
        <Text className="nps-eyebrow">Overall NPS</Text>
        <div className="nps-gauge-wrap" style={{ justifyContent: "center", padding: "22px 0" }}>
          <Skeleton.Avatar active shape="circle" size={140} />
        </div>
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
          <Skeleton.Input active size="small" style={{ width: 90 }} />
          <Skeleton.Input active size="small" style={{ width: 150 }} />
        </div>
        <div className="nps-gauge-avg">
          <Skeleton.Avatar active shape="circle" size={44} />
          <div>
            <Skeleton.Input active size="small" style={{ width: 70, display: "block", marginBottom: 6 }} />
            {/* <Skeleton.Input active size="small" style={{ width: 120 }} /> */}
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="nps-gauge-card" bordered>
      <Text className="nps-eyebrow">Overall NPS</Text>
      <div className="nps-gauge-wrap">
        <svg width="300" height="176" viewBox="0 0 300 176" role="img" aria-label={`Overall NPS score ${score} out of 100`}>
          <path d={arcPath(0.005, 0.495)} fill="none" stroke="#FCECEA" strokeWidth={STROKE} strokeLinecap="round" />
          <path d={arcPath(0.505, 0.745)} fill="none" stroke="#FDF2E3" strokeWidth={STROKE} strokeLinecap="round" />
          <path d={arcPath(0.755, 0.995)} fill="none" stroke="#E9F6EE" strokeWidth={STROKE} strokeLinecap="round" />
          <path d={arcPath(0.5, fraction)} fill="none" stroke="#61C37C" strokeWidth={STROKE} strokeLinecap="round" />
          <circle cx={knobX} cy={knobY} r={9} fill="#fff" stroke="#1E874B" strokeWidth={4} />
          <text x={12} y={172} fontSize={11} fontWeight={500} fill="#8496A8">-100</text>
          <text x={148} y={24} fontSize={11} fontWeight={500} fill="#8496A8" textAnchor="middle">0</text>
          <text x={288} y={172} fontSize={11} fontWeight={500} fill="#8496A8" textAnchor="end">+100</text>
        </svg>
        <div className="nps-gauge-center">
          <div className="nps-gauge-score">{score}</div>
          <div className="nps-gauge-scale">-100 to +100</div>
          <div className={`nps-gauge-delta ${trending ? "up" : "down"}`}>
            {trending ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
            {Math.abs(deltaVsLastMonth)} pts vs last month
          </div>
        </div>
      </div>
      <div className="nps-gauge-avg">
        <NPSAnimatedEmoji score={8} size={44} loop />
        <div>
          <div className="nps-gauge-avg-num">
            {averageScore.toFixed(1)} <span>/ 10</span>
          </div>
          <div className="nps-gauge-avg-label">Average score given</div>
        </div>
      </div>
    </Card>
  );
};

export default NPSGauge;
